import React, { useState } from "react";
import { Button } from "../../components/template";

/**
 * Scoring:
 * A = 2 points
 * B = 1 point
 * C = 0 points
 */

const scoreMap = {
  0: 2,
  1: 1,
  2: 0,
};

const quizData = [
  {
    question: "After eating a regular meal, I usually feel…",
    options: [
      "Comfortable, light, and satisfied",
      "Sometimes heavy or bloated",
      "Very uncomfortable, sleepy, or uneasy",
    ],
  },
  {
    question: "My bowel movements are generally…",
    options: [
      "Regular and easy",
      "Irregular — sometimes fine, sometimes not",
      "Frequently uncomfortable or unpredictable",
    ],
  },
  {
    question: "My appetite through the day feels…",
    options: [
      "Clear and predictable",
      "Up and down",
      "Confusing — either no hunger or sudden strong hunger",
    ],
  },
  {
    question: "I notice that I usually eat my meals…",
    options: [
      "Calmly and with attention",
      "Sometimes rushed, sometimes mindful",
      "Very rushed, distracted, or while multitasking",
    ],
  },
  {
    question: "My energy levels after lunch are usually…",
    options: [
      "Stable and functional",
      "Slightly low or inconsistent",
      "Very low — I feel sleepy or drained",
    ],
  },
  {
    question: "I experience issues like gas, acidity, or indigestion…",
    options: ["Rarely", "Occasionally", "Frequently"],
  },
  {
    question: "My relationship with cravings feels like…",
    options: [
      "I can notice them and choose consciously",
      "I give in sometimes",
      "They feel strong and hard to control",
    ],
  },
  {
    question: "My water intake through the day is…",
    options: [
      "Regular and spaced out",
      "Inconsistent",
      "Either very little or too much at once",
    ],
  },
  {
    question: "Late-night eating for me is…",
    options: ["Rare or very light", "Occasional", "Frequent or heavy"],
  },
  {
    question: "My sleep quality lately feels…",
    options: [
      "Restful and refreshing",
      "Light or interrupted",
      "Poor or unrefreshing",
    ],
  },
];

/* -------------------- INTRO PAGE -------------------- */

const IntroPage = ({ onStart }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
    <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
      <h1 className="text-2xl font-bold text-primary mb-4">
        Gut Health Self-Assessment
      </h1>

      <p className="text-gray-700">
        (Awareness-based, not diagnostic)
      </p>

      <p className="mt-4 text-gray-700">
        I’ll think about my day-to-day digestion and energy,
        not just one bad day.
      </p>

      <p className="mt-3 text-gray-700">
        I’ll choose what feels most true for me
        <strong> most of the time.</strong>
      </p>

      <Button
        variant="primary"
        size="md"
        className="mt-6"
        text="Start Quiz"
        onClick={onStart}
      />
    </div>
  </div>
);

/* -------------------- QUIZ PAGE -------------------- */

const QuizPage = ({ onComplete }) => {
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState(Array(quizData.length).fill(null));
  const [error, setError] = useState("");

  const handleSelect = (index) => {
    const updated = [...answers];
    updated[current] = index;
    setAnswers(updated);
    setError("");
  };

  const handleNext = () => {
    if (answers[current] === null) {
      setError("Please select an option to proceed.");
      return;
    }

    if (current < quizData.length - 1) {
      setCurrent(current + 1);
    } else {
      onComplete(answers);
    }
  };

  const handlePrev = () => {
    setError("");
    if (current > 0) setCurrent(current - 1);
  };

  const progress = ((current + 1) / quizData.length) * 100;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10">
      <div className="w-full max-w-2xl bg-white p-8 rounded-2xl shadow-lg text-center">
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2">
            Question {current + 1} of {quizData.length}
          </p>
          <div className="w-full bg-gray-200 h-2 rounded-full">
            <div
              className="bg-primary h-2 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <p className="text-[#578d48] font-bold text-xl mb-4">
          {quizData[current].question}
        </p>

        <div className="space-y-3">
          {quizData[current].options.map((opt, i) => (
            <label
              key={i}
              className={`flex items-center justify-center p-3 rounded-lg cursor-pointer transition
                ${
                  answers[current] === i
                    ? "bg-primary text-white"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
            >
              <input
                type="radio"
                checked={answers[current] === i}
                onChange={() => handleSelect(i)}
                className="hidden"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>

        {error && <p className="mt-4 text-red-500 text-sm">{error}</p>}
      </div>

      <div className="flex mt-6 space-x-4">
        {current > 0 && (
          <Button text="Previous" variant="whitish" onClick={handlePrev} />
        )}
        <Button
          text={current === quizData.length - 1 ? "Submit" : "Next"}
          variant="primary"
          onClick={handleNext}
        />
      </div>
    </div>
  );
};

/* -------------------- RESULT PAGE -------------------- */

const ResultPage = ({ answers, onRetake }) => {
  const totalScore = answers.reduce(
    (sum, ans) => sum + scoreMap[ans],
    0
  );

  let resultText = "";

  if (totalScore >= 15) {
    resultText = `🌿 : Gut in Relative Balance
Your digestion is generally supporting your energy and wellbeing

Your body is giving clear hunger and fullness signals

Focus: maintain routines and mindful eating habits`;
  } else if (totalScore >= 8) {
    resultText = `🌱 : Mild to Moderate Imbalance
Your gut may be reacting to stress, irregular routines, or eating patterns

Occasional discomfort is a signal, not a problem

Focus: regular meals, slower eating, consistent sleep`;
  } else {
    resultText = `🌧️ : Gut Under Stress
Your digestive system may be overloaded or struggling

This often reflects stress, rushed meals, poor rest, or emotional load

Focus: simplicity, routine, warmth, and gentle care`;
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-light px-6 py-10 text-center">
      <div className="max-w-lg bg-white p-6 rounded-2xl shadow-md">
        <h2 className="text-2xl font-bold text-secondary mb-4">
          Your Gut Health Score
        </h2>

        <p className="text-lg font-semibold text-primary">
          Total Score: {totalScore} / 20
        </p>

        <pre className="whitespace-pre-wrap text-left text-gray-700 mt-4">
          {resultText}
        </pre>

        <Button
          variant="primary"
          size="md"
          className="mt-6"
          text="Retake Quiz"
          onClick={onRetake}
        />
      </div>
    </div>
  );
};

/* -------------------- MAIN COMPONENT -------------------- */

const GutHealthQuiz = () => {
  const [started, setStarted] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [answers, setAnswers] = useState(null);

  return (
    <>
      {!started ? (
        <IntroPage onStart={() => setStarted(true)} />
      ) : completed ? (
        <ResultPage
          answers={answers}
          onRetake={() => {
            setStarted(false);
            setCompleted(false);
            setAnswers(null);
          }}
        />
      ) : (
        <QuizPage
          onComplete={(ans) => {
            setAnswers(ans);
            setCompleted(true);
          }}
        />
      )}
    </>
  );
};

export default GutHealthQuiz;
